/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import useStyles from './useStyles';
import useCommonStyles from '../../useStyles';
import Typography from '../../../../components/Typography/Typography';
import Grid from '../../../../components/Grid/Grid';
import PaperContainer from '../../../../components/PaperContainer/PaperContainer';
import SelectField from '../../../../components/SelectField/SelectField';
import InputField from '../../../../components/InputField/InputField';
import Button from '../../../../components/Button/Button';
import Radio from '../../../../components/Radio/Radio';
import {
  measureStyleSelection,
  notRMeasureStyleSelection,
} from '../../../../constants/configSelections';
import MeasureStyleContent from './MeasureStyleContent';
import { tabDirectionType, qcSinteringManualResult } from '../../../../constants/enums';

const QCSinteringElectrical = ({
  isRStick,
  measureStyle,
  lastMeasureStyle,
  handleMeasureStyleChange,
  handleFetchDifferentMeasureStyle,
  showTable,
  showPaper,
  isLoading,
  isSubmitting, // for disabling submit button when submitting
  currentSubSpec,
  currentSpec,
  measureConfig,
  diffNLMeasure,
  ampSpecConfig,
  specConfig,
  renderRow,
  setRenderRow,
  handleSubSpecChange,
  resultNote,
  handleResultNoteChange,
  handleFormSubmit,
  isLMeasure,
  currentMeasureStyle,
  oneColumn,
  rowLength,
  handleDelete,
  enableDelete,
  disabled,
  sinterManualResult,
  setSinterManualResult,
  originalSinterManualResult,
}) => {
  const timer = useRef(null);
  const { classes } = useStyles();
  const commonClasses = useCommonStyles().classes;
  const [actionBasis, setActionBasis] = useState(tabDirectionType.BY_ITEM);
  const [sinterElectricalManualResult, setSinterElectricalManualResult] =
    useState(sinterManualResult);
  const [outSpecResultList, setOutSpecResultList] = useState([]);

  const onActionBasisChange = (e) => {
    setActionBasis(e.target.value);
  };

  const onSinterElectricalManualResultChange = (e) => {
    setSinterElectricalManualResult(e.target.value === 'true');
    setSinterManualResult(e.target.value === 'true');
  };

  const handleInputBlur = (isOutSpecResult, targetId) => {
    // 只有在尚未有手動結果時，才會更新手動結果，一旦有過手動結果，就不會再更新手動結果
    if (originalSinterManualResult === null) {
      let outSpecResultListLength = outSpecResultList.length;
      if (isOutSpecResult) {
        if (outSpecResultList.includes(targetId)) return;
        setOutSpecResultList((prev) => {
          const newOutSpecResultList = [...prev, targetId];
          outSpecResultListLength = newOutSpecResultList.length;
          return newOutSpecResultList;
        });
      } else if (outSpecResultList.includes(targetId)) {
        setOutSpecResultList((prev) => {
          const newOutSpecResultList = prev.filter((id) => id !== targetId);
          outSpecResultListLength = newOutSpecResultList.length;
          return newOutSpecResultList;
        });
      }
      setSinterElectricalManualResult(outSpecResultListLength === 0);
      setSinterManualResult(outSpecResultListLength === 0);
    }
  };

  const handleClearRow = (targetRowId) => {
    // 清除row時，清除outSpecResultList中對應的rowId
    setOutSpecResultList((prev) => {
      const newOutSpecResultList = prev.filter((id) => id.split('-')[0] !== targetRowId.toString());
      return newOutSpecResultList;
    });
  };

  useEffect(() => {
    if (isLoading) {
      document.body.classList.add('disable-body-scroll');
    } else {
      timer.current = setTimeout(() => {
        document.body.classList.remove('disable-body-scroll');
      }, 500);
    }
    // eslint-disable-next-line consistent-return
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    setSinterElectricalManualResult(sinterManualResult);
  }, [sinterManualResult]);

  useEffect(() => {
    setOutSpecResultList([]);
  }, [measureStyle]);

  useEffect(
    () => () => {
      document.body.classList.remove('disable-body-scroll');
    },
    []
  );

  return (
    showPaper && (
      <PaperContainer className={commonClasses.container} type="QCSinteringElectrical">
        <Typography variant="h6" className={classes.paper_padding_title}>
          填寫量測數據
        </Typography>
        <Grid container direction="column" mt={2.8}>
          {isRStick !== 0 && (
            <>
              <Grid
                item
                container
                alignItems="center"
                justifyContent="space-between"
                className={`${classes.paper_padding}`}
              >
                <Grid item xs={5}>
                  {!oneColumn && (
                    <div>
                      <Radio
                        label="依量測item填寫"
                        name="byItem"
                        value={tabDirectionType.BY_ITEM}
                        selectedValue={actionBasis}
                        onChange={onActionBasisChange}
                      />
                      <Radio
                        label="依量測sample填寫"
                        name="bySample"
                        value={tabDirectionType.BY_SAMPLE}
                        selectedValue={actionBasis}
                        onChange={onActionBasisChange}
                      />
                    </div>
                  )}
                </Grid>
                <Grid item xs={7}>
                  <div className={classes.measure_style_group}>
                    <Typography variant="subtitle1">選擇量測型態：</Typography>
                    <Grid item xs={3}>
                      <SelectField
                        id="measureStyle"
                        placeholder="請選擇量測型態"
                        value={measureStyle === 0 ? currentMeasureStyle : measureStyle}
                        selections={measureStyleSelection}
                        handleChange={handleMeasureStyleChange}
                        className={classes.measure_selection}
                        size="small"
                      />
                    </Grid>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SearchRoundedIcon />}
                      customClasses={classes.select_btn}
                      onClick={handleFetchDifferentMeasureStyle}
                      disabled={isLoading}
                    >
                      查詢
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </>
          )}
          {isRStick === 0 && isLMeasure !== 1 && (
            <>
              <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                className={`${classes.paper_padding} ${classes.title_padding}`}
              >
                {!oneColumn && (
                  <div>
                    <Radio
                      label="依量測item填寫"
                      name="byItem"
                      value={tabDirectionType.BY_ITEM}
                      selectedValue={actionBasis}
                      onChange={onActionBasisChange}
                    />
                    <Radio
                      label="依量測sample填寫"
                      name="bySample"
                      value={tabDirectionType.BY_SAMPLE}
                      selectedValue={actionBasis}
                      onChange={onActionBasisChange}
                    />
                  </div>
                )}
                <Typography
                  variant="subtitle1"
                  className={`${classes.font_weight} ${classes.paper_padding_title}`}
                >
                  量測型態： <b>Z</b>
                </Typography>
              </Grid>
            </>
          )}
          {isRStick === 0 && isLMeasure === 1 && (
            <Grid
              item
              container
              alignItems="center"
              justifyContent="flex-end"
              className={`${classes.paper_padding} ${classes.title_padding}`}
            >
              <Grid item xs={5}>
                {!oneColumn && (
                  <div>
                    <Radio
                      label="依量測item填寫"
                      name="byItem"
                      value={tabDirectionType.BY_ITEM}
                      selectedValue={actionBasis}
                      onChange={onActionBasisChange}
                    />
                    <Radio
                      label="依量測sample填寫"
                      name="bySample"
                      value={tabDirectionType.BY_SAMPLE}
                      selectedValue={actionBasis}
                      onChange={onActionBasisChange}
                    />
                  </div>
                )}
              </Grid>
              <Grid item xs={7}>
                <div className={classes.measure_style_group}>
                  <Typography variant="subtitle1">選擇量測型態：</Typography>
                  <Grid item xs={3}>
                    <SelectField
                      id="measureStyle"
                      placeholder="請選擇量測型態"
                      value={measureStyle === 0 ? currentMeasureStyle : measureStyle}
                      selections={notRMeasureStyleSelection}
                      handleChange={handleMeasureStyleChange}
                      className={classes.measure_selection}
                      size="small"
                    />
                  </Grid>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SearchRoundedIcon />}
                    customClasses={classes.select_btn}
                    onClick={handleFetchDifferentMeasureStyle}
                  >
                    查詢
                  </Button>
                </div>
              </Grid>
            </Grid>
          )}
          <MeasureStyleContent
            showTable={showTable}
            isLoading={isLoading}
            currentSubSpec={currentSubSpec}
            specConfig={specConfig}
            currentSpec={currentSpec}
            measureConfig={measureConfig}
            diffNLMeasure={diffNLMeasure}
            ampSpecConfig={ampSpecConfig}
            renderRow={renderRow}
            handleRowChange={setRenderRow}
            handleSubSpecChange={handleSubSpecChange}
            measureStyle={lastMeasureStyle}
            isLMeasure={isLMeasure}
            isRStick={isRStick}
            tabDirection={actionBasis}
            rowLength={rowLength}
            onInputBlur={handleInputBlur}
            onClearRow={handleClearRow}
          />
        </Grid>

        {showTable && (
          <>
            <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }} gap={2}>
              <Typography variant="subtitle1" sx={{ pr: 1 }}>
                輸入量測結果：
              </Typography>
              <Radio
                label="通過"
                name="resultPass"
                value={qcSinteringManualResult.PASS}
                selectedValue={sinterElectricalManualResult}
                onChange={onSinterElectricalManualResultChange}
                className={classes.result_item}
                disabled={disabled}
              />
              <Radio
                label="不通過"
                name="resultFail"
                value={qcSinteringManualResult.FAIL}
                selectedValue={sinterElectricalManualResult}
                onChange={onSinterElectricalManualResultChange}
                disabled={disabled}
              />
            </Grid>
            <Grid container alignItems="center" wrap="nowrap" className={classes.table_footer}>
              <InputField
                id="note"
                name="note"
                type="text"
                label="備註"
                placeholder="請輸入備註內容"
                value={resultNote}
                onChange={handleResultNoteChange}
              />
              {/* 20230103 Shane - Delete QC record functionality will re-implement in the next phase */}
              {enableDelete && (
                <Button
                  variant="contained"
                  color="error"
                  rounded
                  customClasses={classes.submit_btn}
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={disabled}
                >
                  刪除
                </Button>
              )}

              <Button
                variant="contained"
                color="primary"
                rounded
                customClasses={classes.submit_btn}
                startIcon={<CheckRoundedIcon />}
                onClick={handleFormSubmit}
                disabled={isSubmitting}
              >
                送出
              </Button>
            </Grid>
          </>
        )}
      </PaperContainer>
    )
  );
};

QCSinteringElectrical.defaultProps = {
  originalSinterManualResult: null,
};

QCSinteringElectrical.propTypes = {
  isRStick: PropTypes.bool.isRequired,
  measureStyle: PropTypes.number.isRequired,
  lastMeasureStyle: PropTypes.number.isRequired,
  handleMeasureStyleChange: PropTypes.func.isRequired,
  handleFetchDifferentMeasureStyle: PropTypes.func.isRequired,
  showTable: PropTypes.bool.isRequired,
  showPaper: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  currentSubSpec: PropTypes.string.isRequired,
  currentSpec: PropTypes.array.isRequired,
  measureConfig: PropTypes.array.isRequired,
  ampSpecConfig: PropTypes.array.isRequired,
  diffNLMeasure: PropTypes.array.isRequired,
  specConfig: PropTypes.array.isRequired,
  renderRow: PropTypes.array.isRequired,
  setRenderRow: PropTypes.func.isRequired,
  handleSubSpecChange: PropTypes.func.isRequired,
  resultNote: PropTypes.string.isRequired,
  handleResultNoteChange: PropTypes.func.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  isLMeasure: PropTypes.number.isRequired,
  currentMeasureStyle: PropTypes.number.isRequired,
  oneColumn: PropTypes.bool.isRequired,
  rowLength: PropTypes.number.isRequired,
  handleDelete: PropTypes.func.isRequired,
  enableDelete: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  sinterManualResult: PropTypes.bool.isRequired,
  setSinterManualResult: PropTypes.func.isRequired,
  originalSinterManualResult: PropTypes.bool,
};

export default QCSinteringElectrical;
